import { useState, useEffect, useRef } from "react"
import useLocalStorageState from "use-local-storage-state"
import { useLocation, useNavigate } from "react-router-dom"
import { Kanban } from "../../Utils/kanban/Kanban"
import { IconArrowRight } from "@tabler/icons-react"
import { LeadsDetails } from "../detailPage/leads/LeadsDetails"
import DeleteModal from "../DeleteModal"
import { toast } from "sonner"
import { useGetRequest } from "../../Utils/hooks/QueryHandler"

import { DeleteRequest, PutRequest } from "../../Utils/hooks/ApiHandler"
import nodata from "../../Utils/assets/img/leads_no_data_found_removebg.png"
import { CreatePipelinePage } from "../PipelineComponents/CreatePipelinePage"
import { Button } from "react-bootstrap"
import { usePermissions } from "../../Utils/helpers/commonhelper"
import { AuthorizationCard } from "../../pages/AuthorizationCard"
import { EditPipelinePage } from "../PipelineComponents/EditPipelinePage"
// @ts-ignore
import { CreateLead } from "./CreateLead"
import { LeadsHeader } from "./LeadsHeader"

// Utility function for encoding pipeline IDs for URL parameters
const encodePipelineId = (id) => {
  const base64 = btoa(id.toString())
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

export const Leads = () => {
  // Loading State for delete
  const [loading, setLoading] = useState(false)

  // State management for pipelines, stages, and leads
  const [pipelines, setPipelines] = useState([])
  const [selectedPipelineId, setSelectedPipelineId] = useState(null)
  const [pipelineStages, setPipelineStages] = useState([])
  const [leadsData, setLeadsData] = useState([])
  const [refetchLeadsData, setRefetchLeadsData] = useState(false)
  // Form modal states
  const [showCreateLead, setShowCreateLead] = useState(false)
  const [showLeadDetails, setShowLeadDetails] = useState(false)
  const [selectedLeadId, setSelectedLeadId] = useState(null)
  const [editLeadId, setEditLeadId] = useState(null)
  const [selectedStageId, setSelectedStageId] = useState(null)
  const [settingsDropdown, setSettingsDropdown] = useState(false)
  // Delete confirmation states
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteLeadId, setDeleteLeadId] = useState(null)
  const [showPipelineDeleteModal, setShowPipelineDeleteModal] = useState(false)
  const [pipelineToDelete, setPipelineToDelete] = useState(null)

  // Search states for debounced search
  const [debouncedSearch, setDebouncedSearch] = useState("")


  const [editPipeline, setEditPipeline] = useState(false)
  const [encodedId, setEncodedId] = useState(null)

  const [createPipeline, setCreatePipeline] = useState(false)
  // Pagination states for list view
  const [limit, setLimit] = useState(25)
  const [listOffset, setListOffset] = useState(0)
  const [cardOffset, setCardOffset] = useState(0)

  // Local storage for view settings
  const [currView, setCurrView] = useState(() => {
    return localStorage.getItem("leadsView") || "card"
  })
  const activeOffset = currView === "card" ? cardOffset : listOffset
  const setActiveOffset =
    currView === "card" ? setCardOffset : setListOffset

  // Filter type state with localStorage persistence
  const [currentFilterType, setCurrentFilterType] = useState(() => {
    return localStorage.getItem("leadsFilterType") || "all"
  })

  // Session storage and authentication setup
  const [customer, setCustomer] = useLocalStorageState("customer")
  const navigate = useNavigate()
  const customerId = customer?.customer_details?.customer_id
  const loginUserId = customer?.customer_details?.id
  const token = customer?.token?.authorization
  const rf = customer?.token?.authentication

  // Tab focus tracking for handling tab switches
  const [isTabVisible, setIsTabVisible] = useState(true)
  const [forceRefetch, setForceRefetch] = useState(0)
  const mountedRef = useRef(true)

  // Permanent fields that are always visible in the table
  const permanentFields = [
    "contact_full_name",
    "contact_phone",
    "email",
    "requirement",
    "budget",
    "lead_status",
    "pipelineStageName",
    "source_title"
  ]

  // // Available lead fields for column configuration (excluding permanent fields)
  const columnOptions = [
    { id: "name", label: "Name", mandatory: true },
    { id: "contact_phone", label: "Phone" },
    { id: "email", label: "Email" },
    { id: "requirement", label: "Requirement" },
    { id: "pipelineStageName", label: "Pipeline Stage Name" },
    { id: "lead_status", label: "Lead Status" },
    { id: "budget", label: "Budget" },
    { id: "source_title", label: "Source" },
    { id: "expected_closure_date", label: "Expected Closure Date" },
    { id: "owner", label: "Lead Owner" },
    // { id: "followup_date", label: "Follow Up Date" },
    // { id: "expected_closure_time", label: "Expected Closure Time" },
    // { id: "followup_time", label: "Follow Up Time" },
  ]


  // Initialize columns for the table - ALL columns visible by default
  const [columns, setColumns] = useState(() => {
    const saved = localStorage.getItem("dealsVisibleColumns");
    const allColumns = columnOptions.map(col => col.label);

    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Error parsing visible columns:", e);
      }
    }
    // Default: all columns visible
    return allColumns;
  });

  // Filter states
  const [filterStates, setFilterState] = useState([])
  const [filteredLeads, setFilteredLeads] = useState(null)

  const [endpoint, setEndpoint] = useState("")
  const [queryKey, setQueryKey] = useState("")

  const [heading, setHeading] = useState("All Leads");
  // state for filter dropdown of quotations
  const [filterDropdown, setFilterDropdown] = useState(false);

  // filter categories to render within the heading dropdown
  const [filterCategories, setFilterCategories] = useState([])

  // for storing the pipeline data to pass down to the advance filtering component
  const [filterDropPipelineStage, setFilterDropPipelineStage] = useState([])

  // filter state to see the temoporary applied filter data,
  const [tempFilterData, setTempFilterData] = useState(null);

  // API headers configuration
  const headers = {
    Authentication: rf,
    Authorization: token,
  }

  const extras = {
    customer,
    setCustomer,
    rf,
    navigate,
  }

  // permission setup for leads
  const permission = customer.customer_details?.permission
  // permission for lead
  const leadPermission = permission?.filter(c => c.module_name === "leads")
  const user_type = Number(customer.customer_details?.user_type)

  const leadGrant = {
    isView: user_type !== 1 ? Boolean(Number(leadPermission?.find(c => c.feature_name === "leads")?.view)) : true,
    isCreate: user_type !== 1 ? Boolean(Number(leadPermission?.find(c => c.feature_name === "leads")?.create)) : true,
    isEdit: user_type !== 1 ? Boolean(Number(leadPermission?.find(c => c.feature_name === "leads")?.edit)) : true,
    isDelete: user_type !== 1 ? Boolean(Number(leadPermission?.find(c => c.feature_name === "leads")?.delete)) : true,
    isImport: user_type !== 1 ? Boolean(Number(leadPermission?.find(c => c.feature_name === "import")?.is_action)) : true,
    isExport: user_type !== 1 ? Boolean(Number(leadPermission?.find(c => c.feature_name === "export")?.is_action)) : true,
  }
  const followupGrant = {
    isView: user_type !== 1 ? Boolean(Number(leadPermission?.find(c => c.feature_name === "followup")?.view)) : true,
    isCreate: user_type !== 1 ? Boolean(Number(leadPermission?.find(c => c.feature_name === "followup")?.create)) : true,
    isEdit: user_type !== 1 ? Boolean(Number(leadPermission?.find(c => c.feature_name === "followup")?.edit)) : true,
    isDelete: user_type !== 1 ? Boolean(Number(leadPermission?.find(c => c.feature_name === "followup")?.delete)) : true,
  }
  const leadPipelineGrant = {
    isView: user_type !== 1 ? Boolean(Number(leadPermission?.find(c => c.feature_name === "pipeline")?.view)) : true,
    isCreate: user_type !== 1 ? Boolean(Number(leadPermission?.find(c => c.feature_name === "pipeline")?.create)) : true,
    isEdit: user_type !== 1 ? Boolean(Number(leadPermission?.find(c => c.feature_name === "pipeline")?.edit)) : true,
    isDelete: user_type !== 1 ? Boolean(Number(leadPermission?.find(c => c.feature_name === "pipeline")?.delete)) : true,
  }


  // Track tab visibility and handle refetch on focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden
      setIsTabVisible(isVisible)

      if (isVisible && mountedRef.current) {
        // Force refetch when tab becomes visible again
        setTimeout(() => {
          setForceRefetch(prev => prev + 1)
        }, 100)
      }
    }

    const handleFocus = () => {
      if (mountedRef.current) {
        setIsTabVisible(true)
        setTimeout(() => {
          setForceRefetch(prev => prev + 1)
        }, 100)
      }
    }

    const handleBlur = () => {
      setIsTabVisible(false)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Save view preference to localStorage
  useEffect(() => {
    localStorage.setItem("leadsView", currView)
  }, [currView])

  // Save visible columns to localStorage
  useEffect(() => {
    localStorage.setItem("leadsVisibleColumns", JSON.stringify(columns))
  }, [columns])

  // Save filter type to localStorage
  useEffect(() => {
    localStorage.setItem("leadsFilterType", currentFilterType)
  }, [currentFilterType])

  // Fetch all pipelines for the customer
  const {
    data: pipelineData,
    isLoading: isPipelineLoading,
    refetch: refetchPipeline,
  } = useGetRequest(`lead/fetchPipelines/${customerId}/leads/${loginUserId}/${customer.customer_details?.user_type}`, ["fetchLeadsPipeline", forceRefetch], headers, extras, {})
  // Handle pipeline data updates and set default selection
  useEffect(() => {
    if (pipelineData?.status === "success") {
      const fetchedPipelines = pipelineData.data || []
      setPipelines(fetchedPipelines)

      if (!selectedPipelineId || !fetchedPipelines.some((p) => p.id === selectedPipelineId)) {
        if (fetchedPipelines.length > 0) {
          const selectpipe = fetchedPipelines.find((el) => el.is_active === "yes") || fetchedPipelines[0]
          setSelectedPipelineId(selectpipe.id)
        }
      }
    } else if (pipelineData?.status === "fail") {
      setPipelines([])
      setSelectedPipelineId(null)
      if (pipelineData.message) {
        toast.error(pipelineData.message)
      }
    }
  }, [pipelineData])

  const pipelineStageUrl = selectedPipelineId ? `lead/fetchPipelineStage/${selectedPipelineId}/${customerId}` : null

  // Fetch pipeline stages for selected pipeline
  const {
    data: pipelineStageData,
    isLoading: isPipelineStageLoading,
    refetch: refetchPipelineStage,
  } = useGetRequest(
    pipelineStageUrl,
    ["fetchPipelineStage", selectedPipelineId, forceRefetch],
    headers,
    extras,
    {
      enabled: !!selectedPipelineId && isTabVisible,
    }
  )

  // // Handle pipeline stage data updates and formatting
  useEffect(() => {
    if (pipelineStageData?.status === "success") {
      const formattedStages = pipelineStageData.data.map((stage) => ({
        ...stage,
        stage_id: stage.id,
        sequence: String(stage.sequence),
        probability: Math.min(100, stage.probability || 0),
        rotting_days: stage.rotting_days === null ? null : stage.rotting_days,
        rotting_enabled: stage.rotting_days !== null && stage.rotting_days > 0,
      }))
      const filterDropdownData = pipelineStageData.data.map((c) => ({
        stage_id: c.id,
        stage_name: c.stage_name
      }))
      setPipelineStages(formattedStages)
      setSelectedStageId(formattedStages[0]?.id)
      setFilterDropPipelineStage(filterDropdownData)
    } else if (pipelineStageData?.status === "fail") {
      setPipelineStages([])
      if (pipelineStageData.message) {
        toast.error(pipelineStageData.message)
      }
    }
  }, [pipelineStageData, heading])

  // Create params object
  const leadsParams = {}
  if (currView === "list") {
    leadsParams.limit = limit
    leadsParams.offset = activeOffset
  }
  if (debouncedSearch) {
    leadsParams.search = debouncedSearch
  }

  const location = useLocation()
  const contactRedirectData = location?.state || null
  useEffect(() => {
    if (!selectedPipelineId) return
    // fetching contacts based on header
    const lowerHeading = heading
      ? heading?.toLowerCase()?.split(" ").join("_")
      : heading;
    if (lowerHeading === "leads_created_by_me") {
      setEndpoint(
        `lead/leadsCreatedByMe/${loginUserId}/${customerId}/${selectedPipelineId}/${customer.customer_details?.user_type}/${currView}?module_name=${contactRedirectData?.module_name}&contact_id=${contactRedirectData?.contactId}`
      );
      setQueryKey("leadsCreatedByMe");
    } else if (lowerHeading === "leads_created_today") {
      setEndpoint(
        `lead/getLeadsCreatedBasedOnDuration/${customerId}/today/${selectedPipelineId}/${customer?.customer_details?.id}/${customer.customer_details?.user_type}/${currView}?module_name=${contactRedirectData?.module_name}&contact_id=${contactRedirectData?.contactId}`
      );
      setQueryKey("leadsCreatedToday");
    } else if (lowerHeading === "leads_created_this_month") {
      setEndpoint(
        `lead/getLeadsCreatedBasedOnDuration/${customerId}/month/${selectedPipelineId}/${customer?.customer_details?.id}/${customer.customer_details?.user_type}/${currView}?module_name=${contactRedirectData?.module_name}&contact_id=${contactRedirectData?.contactId}`
      );
      setQueryKey("leadsCreatedThisMonth");
    } else if (lowerHeading === "my_today_followup") {
      setEndpoint(
        `lead/fetchLeadsBasedOnTodaysFollowup/${customerId}/${selectedPipelineId}/${customer?.customer_details?.id}/${customer.customer_details?.user_type}/${currView}/filter_card_id=null`
      );
      setQueryKey("mytodayfollowup");
    }
    else if (lowerHeading === "lost_leads") {
      setEndpoint(
        `lead/getLeadsBasedOnStatus/${customerId}/${selectedPipelineId}/lost/${customer?.customer_details?.id}/${customer.customer_details?.user_type}/${currView}/filter_card_id=null`
      );
      setQueryKey("fetchLostLeads");
    } else if (lowerHeading === "unqualified_leads") {
      setEndpoint(
        `lead/getLeadsBasedOnStatus/${customerId}/${selectedPipelineId}/unqualified/${customer?.customer_details?.id}/${customer.customer_details?.user_type}/${currView}/filter_card_id=null`
      );
      setQueryKey("fetchUnqualifiedLeads");
    } else if (lowerHeading === "all_leads") {
      setEndpoint(
        `lead/fetchLeads/${customerId}/${selectedPipelineId}/${customer?.customer_details?.id}/${customer.customer_details?.user_type}/${currView}?module_name=${contactRedirectData?.module_name}&contact_id=${contactRedirectData?.contactId}`
      );
      setQueryKey("fetchAllLeads");

    } else {
      const matchedCategory = filterCategories.find((category) => {
        return category.card_name === heading;
      });
      if (matchedCategory) {
        setEndpoint(
          `lead/fetchLeads/${customerId}/${selectedPipelineId}/${customer?.customer_details?.id}/${customer.customer_details?.user_type}/${currView}/${matchedCategory.id}`
        );
        setQueryKey(
          `filterQuery${matchedCategory.card_name
            .toLowerCase()
            ?.split(" ")
            .join("_")}`
        );
      }
    }
  }, [heading, filterCategories, selectedPipelineId, currView]);

  // for storing the parsed shared users to pass down to the pipeline share model where i will render these users with their respective permissions corresponding to that pipeline
  const [parsedSharedUsers, setParsedSharedUsers] = useState(null)

  const {
    data: leadsApiData,
    isLoading: isLeadsLoading,
    refetch: refetchLeads,
  } = useGetRequest(
    endpoint,
    [queryKey, selectedPipelineId, currentFilterType, forceRefetch, activeOffset, limit, debouncedSearch],
    headers,
    { customer, setCustomer, rf: headers.Authentication, navigate },
    {
      ...leadsParams,
      enabled: !!selectedPipelineId && isTabVisible && currView
    }
  );

  useEffect(() => {
    const sharedUsers = leadsApiData?.pipeline_data?.["shared_to"]
    setParsedSharedUsers(JSON.parse(sharedUsers !== undefined && sharedUsers) || [])

    setLeadsData(
      leadsApiData?.data?.map(stage => ({
        ...stage,
        leads: stage.leads?.map(lead => ({
          ...lead,
          product_services:
            typeof lead.product_services === "string"
              ? (() => {
                try {
                  return JSON.parse(lead.product_services)
                } catch {
                  return lead.product_services
                }
              })()
              : lead.product_services
        }))
      }))
    );
  }, [leadsApiData, queryKey])

  // hook for getting the permissions assigned to the user
  const { permissionAssigned, canEdit_canDownload } = usePermissions(customer, parsedSharedUsers, leadsApiData)
  useEffect(() => {
    if (!selectedPipelineId) return;

    if (currView === "list") {
      setActiveOffset(0);
    }

    const timer = setTimeout(() => {
      setForceRefetch(prev => prev + 1);
    }, 100);

    return () => clearTimeout(timer);
  }, [currView, selectedPipelineId]);

  // Force refetch when tab becomes visible and data is stale
  useEffect(() => {
    if (isTabVisible && selectedPipelineId && forceRefetch > 0) {
      const timer = setTimeout(() => {
        refetchLeads()
        refetchPipelineStage()
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [forceRefetch, isTabVisible, selectedPipelineId, refetchLeads, refetchPipelineStage])

  // Enhanced pipeline change handling
  useEffect(() => {
    if (!selectedPipelineId) return

    // Clear dependent data immediately
    setPipelineStages([])
    setSelectedStageId(null)
    setShowCreateLead(false)
    // setShowFollowUp(false)
    setSelectedLeadId(null)
    setShowLeadDetails(false)

    // Trigger refetch with a delay to ensure clean state
    const timer = setTimeout(() => {
      setForceRefetch(prev => prev + 1)
    }, 50)

    return () => clearTimeout(timer)
  }, [selectedPipelineId])

  // Handle mutual exclusion of filter and settings dropdowns
  useEffect(() => {
    if (filterDropdown && settingsDropdown) {
      setSettingsDropdown(false)
    }
  }, [filterDropdown])

  useEffect(() => {
    if (settingsDropdown && filterDropdown) {
      setFilterDropdown(false)
    }
  }, [settingsDropdown])

  // Update lead stage with optimistic UI updates
  const handleUpdateLead = async (leadData, newStageId, oldStageId) => {
    const originalLeadsData = [...leadsData]

    // Optimistically update UI
    setLeadsData((prev) => {
      const newData = [...prev]

      const oldStageIndex = newData.findIndex((stage) => stage.stage_id === oldStageId)
      if (oldStageIndex !== -1) {
        newData[oldStageIndex] = {
          ...newData[oldStageIndex],
          leads: newData[oldStageIndex].leads.filter((lead) => lead.lead_id !== leadData.lead_id),
        }
      }

      const newStageIndex = newData.findIndex((stage) => stage.stage_id === newStageId)
      if (newStageIndex !== -1) {
        newData[newStageIndex] = {
          ...newData[newStageIndex],
          leads: [...newData[newStageIndex].leads, leadData],
        }
      }

      return newData
    })

    const selectedPipeline = pipelines.find((p) => p.id === selectedPipelineId)
    const updatePayload = {
      customer_id: customer.customer_details?.customer_id,
      user_login_id: loginUserId,
      owner_id: leadData.owner_id,
      lead_id: leadData.lead_id,
      contact_id: leadData.contact_id,
      pipeline_id: selectedPipeline.id,
      pipeline_stage_id: newStageId,
      expected_closure_date: leadData.expected_closure_date,
      expected_closure_time: leadData.expected_closure_time,
      requirement: leadData.requirement,
      product_services: leadData.product_services,
      budget: leadData.budget,
      lead_source_id: leadData.lead_source_id,
    }
    try {
      const response = await PutRequest(`lead/updateLead/`, updatePayload, { headers }, extras)

      if (response?.status === "success") {
        toast.success(response.message || "Lead updated successfully")
        await refetchLeads()
      } else {
        toast.error(response?.message || "Failed to update lead")
        setLeadsData(originalLeadsData)
      }
    } catch (error) {
      toast.error(error.message || "Failed to save lead")
      setLeadsData(originalLeadsData)
    }
  }

  // Confirm and execute pipeline deletion
  const handlePipelineDeleteConfirm = async () => {
    if (!pipelineToDelete) return

    try {
      setLoading(true)
      const response = await DeleteRequest(`lead/deletePipeline/${pipelineToDelete}/${customerId}/${customer.customer_details?.id}`, { headers }, extras)
      if (response?.status === "success") {
        toast.success("Pipeline deleted successfully")
        setShowPipelineDeleteModal(false)
        setPipelineToDelete(null)
        setLoading(false)
        await refetchPipeline()
        navigate(`/ leads`)
      } else {
        toast.error(response?.message || "Failed to delete pipeline")
        setLoading(false)
      }
    } catch (error) {
      toast.error("An error occurred while deleting pipeline")
      setLoading(false)
    }
  }
  // Open add lead form
  const handleAddLead = (stageId = null) => {
    if (!leadGrant?.isCreate) {
      toast.warning("You are not authorised for this action!")
      return
    }
    if (!canEdit_canDownload) {
      toast.warning("You are not authorised for this action!")
      return
    }
    setSelectedStageId(stageId)
    setEditLeadId(null)
    setShowCreateLead(true)
  }

  // Open edit lead form with specific lead ID
  const handleEditLead = (leadId) => {
    setEditLeadId(leadId)
    setSelectedStageId(null)
    setShowCreateLead(true)
  }

  // // Open follow up form for specific lead
  // const handleFollowUpLead = (leadId) => {
  //   if (!followupGrant.isView) {
  //     toast.warning("You are not authorized to view lead's follow-up!")
  //     return
  //   }
  //   setFollowUpLeadId(leadId)
  //   setShowFollowUp(true)
  // }

  // Show delete confirmation modal for lead
  const handleDeleteLead = (leadId) => {
    setDeleteLeadId(leadId)
    setShowDeleteModal(true)
  }

  // Open lead details modal with proper lead ID
  const handleOpenLeadDetails = (lead) => {
    setShowLeadDetails(false)
    setSelectedLeadId(null)

    setTimeout(() => {
      setSelectedLeadId(lead.lead_id)
      setShowLeadDetails(true)
    }, 50)
  }

  // Close lead details and refresh data
  const handleCloseLeadDetails = () => {
    setShowLeadDetails(false);
    setSelectedLeadId(null);
    setRefetchLeadsData(true);
  };


  // Confirm and execute lead deletion
  const handleDeleteConfirm = async () => {
    try {
      setLoading(true)
      const response = await DeleteRequest(`lead/deleteLead/${deleteLeadId}/${loginUserId}`, { headers }, extras)
      if (response.status === "fail" || response.success === false) {
        toast.error(response.message)
        setLoading(false)
        return
      }
      toast.success(response.message)
      setLoading(false)
      await refetchLeads()

    } catch (error) {
      toast.error("An error occurred while deleting lead")
      setLoading(false)
    } finally {
      setShowDeleteModal(false)
      setDeleteLeadId(null)
      setLoading(false)
    }
  }

  // Close delete confirmation modal
  const handleDeleteClose = () => {
    setShowDeleteModal(false)
    setDeleteLeadId(null)
    setLoading(false)
  }

  // Refresh leads after update
  const handleLeadUpdated = () => {
    refetchLeads()
  }

  // Close create lead form and reset selection
  const handleCreateLeadClose = () => {
    setShowCreateLead(false)
    setSelectedStageId(null)
  }

  // Pagination handlers
  const handlePageChange = (selectedPage) => {
    setActiveOffset(selectedPage.selected)
  }

  // Enhanced refetch handling
  useEffect(() => {
    if (refetchLeadsData && isTabVisible) {
      refetchLeads();
      setRefetchLeadsData(false);
    }
  }, [refetchLeadsData, refetchLeads, isTabVisible]);

  // Calculate pagination values
  const totalContacts = leadsApiData?.total_count || 0
  const pageCount = Math.ceil(totalContacts / limit)

  const selectedPipeline = pipelines.find((p) => p.id === selectedPipelineId)

  // Enhanced loading state and data ready check
  const isDataReady = selectedPipeline &&
    pipelineStages.length > 0 &&
    !isPipelineStageLoading &&
    !isLeadsLoading

  if (editPipeline && encodedId) {
    return (
      <EditPipelinePage
        encodedId={encodedId}
        module_name="lead"
        refetch={refetchPipeline}
        refetchPipelineStage={refetchPipelineStage}
        setEditPipeline={setEditPipeline}
        pipelineCount={pipelines.length}
      />
    )
  }
  if (createPipeline) {
    return (
      <CreatePipelinePage
        module_name="lead"
        refetchPipelineData={refetchPipeline}
        setCreatePipeline={setCreatePipeline}
        setSelectedPipelineId={setSelectedPipelineId}
      />
    )
  }
  return (
    <>
      {/* header component for leads */}
      <LeadsHeader
        heading={heading}
        setHeading={setHeading}
        permissionAssigned={permissionAssigned}
        canEdit_canDownload={canEdit_canDownload}
        filterCategories={filterCategories}
        setFilterCategories={setFilterCategories}
        filterDropPipelineStage={filterDropPipelineStage}
        leadData={leadsData}
        filterDropdown={filterDropdown}
        setFilterDropdown={setFilterDropdown}
        pipelines={pipelines}
        selectedPipelineId={selectedPipelineId}
        setSelectedPipelineId={setSelectedPipelineId}
        setDebouncedSearch={setDebouncedSearch}
        currView={currView}
        setCurrView={setCurrView}
        leadGrant={leadGrant}
        leadPipelineGrant={leadPipelineGrant}
        setCreatePipeline={setCreatePipeline}
        encodePipelineId={encodePipelineId}
        handleAddLead={handleAddLead}
        setEditPipeline={setEditPipeline}
        setActiveOffset={setActiveOffset}
        setEncodedId={setEncodedId}
        selectedPipeline={selectedPipeline}
        parsedSharedUsers={parsedSharedUsers}
        selectedStageId={selectedStageId}
        filterStates={filterStates}
        setFilterState={setFilterState}
        leadsApiData={leadsApiData}
        queryKey={queryKey}
        setTempFilterData={setTempFilterData}
        refetchLeads={refetchLeads}
        columns={columns}
        setColumns={setColumns}
        columnOptions={columnOptions}
      />

      {/* Enhanced loading state with better UX */}
      {
        (isPipelineStageLoading || isLeadsLoading) && leadGrant?.isView && (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )
      }

      {/* Enhanced condition for showing "No data" state */}
      {
        !isDataReady && !isPipelineStageLoading && !isLeadsLoading && selectedPipelineId && isTabVisible && leadGrant?.isView && (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
            <div className="text-center">
              <div
                class="d-flex justify-content-center align-items-center"
              >
                <img
                  src={nodata}
                  alt=""
                  style={{ width: "17rem", marginTop: "0" }}
                />
              </div>
              <Button
                onClick={() => {
                  const encodedId = encodePipelineId(selectedPipelineId)
                  setEncodedId(encodedId)
                  setEditPipeline(true)
                }}
                className="btn-sm btn-default"
              >
                Create Stage <IconArrowRight size={14} stroke={2} />
              </Button>
            </div>
          </div>
        )
      }
      {
        pipelines.length === 0 && !isPipelineLoading && !isPipelineStageLoading && leadGrant?.isView &&
        <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
          <div className="text-center">
            <p>No data available</p>
            <Button
              variant="outline-primary"
              onClick={() => setCreatePipeline(true)}
              className="btn-sm"
            >
              Create Your first Pipeline
            </Button>
          </div>
        </div>
      }

      {/* Kanban board - Enhanced rendering logic */}
      {
        leadGrant?.isView ?
          isDataReady && (
            <Kanban
              stages={pipelineStages}
              canEdit_canDownload={canEdit_canDownload}
              leadsData={leadsData}
              setLeadsData={setLeadsData}
              onUpdateLead={handleUpdateLead}
              onRefetchLeads={refetchLeads}
              pipelineName={selectedPipeline.pipeline_name}
              onAddLead={handleAddLead}
              onEditLead={handleEditLead}
              // onFollowUpLead={handleFollowUpLead}
              onDeleteLead={handleDeleteLead}
              currView={currView}
              visibleColumns={columns}
              filteredLeads={filteredLeads}
              onOpenLeadDetails={handleOpenLeadDetails}
              totalContacts={totalContacts}
              offset={activeOffset}
              limit={limit}
              pageCount={pageCount}
              handlePageChange={handlePageChange}
              setOffset={setActiveOffset}
              setLimit={setLimit}
              mainGrant={leadGrant}
              tempFilterData={tempFilterData}
              followupGrant={followupGrant}
              headers={headers}
              extras={extras}
              module_name={'lead'}
            />
          )
          :
          <AuthorizationCard />
      }

      {/* Empty state for when data is ready but no leads exist */}
      {/* {
        isDataReady && !isLeadsLoading && leadsData.length > 0 && (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
            <div className="text-center">
              <h5>No leads found</h5>
              <p>Start by adding your first lead to this pipeline.</p>
              <Button
                variant="primary"
                onClick={() => handleAddLead()}
                className="btn-sm"
              >
                <FontAwesomeIcon icon={faPlus} /> Add Lead
              </Button>
            </div>
          </div>
        )
      } */}

      {/* Create/Edit Lead Modal */}
      <CreateLead
        show={showCreateLead}
        onHide={handleCreateLeadClose}
        setLeadClose={setShowCreateLead}
        editId={editLeadId}
        stageId={selectedStageId}
        pipelineId={selectedPipelineId}
        refetchLeads={refetchLeads}
        onLeadUpdated={handleLeadUpdated}
      />

      {
        selectedLeadId &&
        <LeadsDetails
          show={showLeadDetails}
          canEdit_canDownload={canEdit_canDownload}
          setRefetchLeadsData={setRefetchLeadsData}
          onClose={handleCloseLeadDetails}
          onEdit={handleEditLead}
          onDelete={handleDeleteLead}
          // onFollowUp={handleFollowUpLead}
          leadId={selectedLeadId}
          pipelineStageData={pipelineStages}
          pipelines={pipelines}
        />
      }

      {/* Delete Lead Confirmation Modal */}
      <DeleteModal
        show={showDeleteModal}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        isLoading={loading}
        message="Are you sure you want to delete this lead?"
      />

      {/* Pipeline Delete Confirmation Modal */}
      <DeleteModal
        show={showPipelineDeleteModal}
        onClose={() => {
          setShowPipelineDeleteModal(false)
          setPipelineToDelete(null)
        }}
        onConfirm={handlePipelineDeleteConfirm}
        isLoading={loading}
        message="Are you sure you want to permanently delete this pipeline?"
      />

    </>
  )
}

export default Leads